import axios from 'axios';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const SESSION_KEY = 'yt-session-id';
function getSessionId() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = (crypto && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now();
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

const api = axios.create({ baseURL: API_BASE, withCredentials: true });
api.interceptors.request.use((config) => {
  // Attach an anonymous session id for guests (used for scoping likes/candles
  // for logged-out viewers). Authenticated requests use the httpOnly access
  // cookie instead.
  const headers = config.headers || {};
  const alreadySet = Object.keys(headers).some(k => k.toLowerCase() === 'x-session-id');
  if (!alreadySet) {
    headers['X-Session-Id'] = getSessionId();
    config.headers = headers;
  }
  return config;
});

// Response interceptor — reports UNEXPECTED failures to Sentry.
// Deliberately NOT reporting 4xx (regular business flow — bad password,
// missing prayer, validation) so we don't drown in noise. Only report:
//   • 5xx (real server errors)
//   • Network / timeout errors (no `response`)
// The interceptor is a lazy `require` of Sentry so importing api.js
// from Node tests (or before instrument.js loads) doesn't blow up.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    try {
      const status = error?.response?.status;
      const isNetworkOrTimeout = !error?.response;
      const shouldReport = isNetworkOrTimeout || (typeof status === 'number' && status >= 500);
      if (shouldReport) {
        const { Sentry } = await import('../instrument');
        Sentry.withScope((scope) => {
          scope.setTag('api_status', String(status || 'network'));
          scope.setContext('api', {
            method: error.config?.method,
            // Path only — never full URL with query, keeps secrets out.
            path: error.config?.url,
            request_id: error.response?.headers?.['x-request-id'],
          });
          Sentry.captureException(error);
        });
      }
    } catch { /* interceptor must never crash */ }
    return Promise.reject(error);
  }
);

export default api;

// ---------- Likes ----------
export async function getLikeState(videoId) {
  const { data } = await api.get(`/likes/${videoId}`);
  return data; // { state, like_count, dislike_count }
}
export async function toggleLike(videoId, state) {
  const { data } = await api.post(`/likes/${videoId}/toggle`, { state });
  return data;
}
export async function getMyLiked() {
  const { data } = await api.get(`/likes/me/list`);
  return data.video_ids || [];
}

// ---------- Comments ----------
export async function listComments(videoId) {
  const { data } = await api.get(`/comments`, { params: { video_id: videoId } });
  return data;
}
export async function createComment(videoId, text) {
  const { data } = await api.post(`/comments`, { video_id: videoId, text });
  return data;
}
export async function deleteComment(id) {
  // Legacy video-comment endpoint (unused by the prayer app, kept for the
  // legacy Watch.jsx page). Renamed on the server to /video-comments to
  // free up /comments/{id} for the prayer owner-comments feature.
  const { data } = await api.delete(`/video-comments/${id}`);
  return data;
}

// ---------- History ----------
export async function addHistory(videoId) {
  const { data } = await api.post(`/history`, { video_id: videoId });
  return data;
}
export async function getHistory() {
  const { data } = await api.get(`/history`);
  return data;
}
export async function clearHistory() {
  const { data } = await api.delete(`/history`);
  return data;
}

// ---------- Playlists ----------
export async function getPlaylists() {
  const { data } = await api.get(`/playlists`);
  return data;
}
export async function createPlaylist(name) {
  const { data } = await api.post(`/playlists`, { name });
  return data;
}
export async function addToPlaylist(playlistId, videoId) {
  const { data } = await api.post(`/playlists/${playlistId}/add`, { video_id: videoId });
  return data;
}
export async function deletePlaylist(playlistId) {
  const { data } = await api.delete(`/playlists/${playlistId}`);
  return data;
}

// ---------- Watch Later ----------
export async function getWatchLater() {
  const { data } = await api.get(`/watch-later`);
  return data.video_ids || [];
}
export async function toggleWatchLater(videoId) {
  const { data } = await api.post(`/watch-later/toggle`, { video_id: videoId });
  return data;
}

// ---------- Subscriptions ----------
export async function getSubs() {
  const { data } = await api.get(`/subscriptions`);
  return data.channel_ids || [];
}
export async function toggleSub(channelId) {
  const { data } = await api.post(`/subscriptions/${channelId}/toggle`);
  return data;
}

// ---------- Trending ----------
export async function getTrending() {
  const { data } = await api.get(`/feed/trending`);
  return data.video_ids || [];
}


// ---------- Auth / Profile ----------
export async function getMe() {
  // Lightweight session peek — returns the current user object or
  // throws (401) when the cookie is missing / stale. Used by the
  // AuthModal "Check your email" panel to detect verification that
  // happened in a sibling tab (safety net for the BroadcastChannel).
  const { data } = await api.get(`/auth/me`);
  return data;
}
export async function updateProfile(updates = {}) {
  // Whitelist the fields the backend PATCH /auth/me actually accepts. The
  // previous implementation destructured only `name` and `zip_code`, which
  // meant `narration_preference` (and `default_home_tab`) passed in from
  // Settings were SILENTLY DROPPED before hitting the network — the request
  // body ended up as `{}` and no change ever persisted. Enumerating the
  // full set here is explicit + safe + easy to extend when a new field is
  // added to the backend UpdateProfileIn model.
  const ALLOWED = ['name', 'handle', 'zip_code', 'bio', 'narration_preference', 'system_voice_gender', 'system_voice_id', 'default_home_tab', 'nearby_radius_mi', 'gender', 'religion', 'display_religions', 'display_religions_filter_enabled', 'notification_prefs', 'show_circle_stats'];
  const body = {};
  for (const key of ALLOWED) {
    if (updates[key] !== undefined) body[key] = updates[key];
  }
  const { data } = await api.patch(`/auth/me`, body);
  return data;
}
export async function changePassword(current_password, new_password) {
  const { data } = await api.post(`/auth/change-password`, { current_password, new_password });
  return data;
}
export async function forgotPassword(email) {
  // Fire-and-forget from the caller's point of view. Backend always
  // returns 200 to avoid account-enumeration leaks (see the endpoint
  // comment).
  const { data } = await api.post(`/auth/forgot-password`, { email });
  return data; // { ok: true }
}
export async function resetPassword(token, new_password) {
  const { data } = await api.post(`/auth/reset-password`, { token, new_password });
  return data; // { ok: true }
}
export async function checkHandleAvailable(handle) {
  const { data } = await api.get(`/auth/handle-available`, { params: { handle } });
  return data; // { available, reason, handle }
}
export async function checkEmailAvailable(email) {
  const { data } = await api.get(`/auth/email-available`, { params: { email } });
  return data; // { available, reason, email }
}
export async function uploadAvatar(file) {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post(`/auth/me/avatar`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

// ---------- Prayers ----------
export async function listPrayers({ limit = 50, skip = 0, user_id = null, sort = null, viewer_zip = null, hashtag = null, q = null, nearby_radius_mi = null } = {}) {
  const params = { limit, skip };
  if (user_id) params.user_id = user_id;
  if (sort) params.sort = sort;
  if (viewer_zip) params.viewer_zip = viewer_zip;
  if (hashtag) params.hashtag = hashtag;
  if (q) params.q = q;
  if (nearby_radius_mi) params.nearby_radius_mi = nearby_radius_mi;
  const { data } = await api.get(`/prayers`, { params });
  return data;
}
export async function fetchForYouPrayers({ limit = 60 } = {}) {
  // Personalised "All" tab feed — server ranks by the viewer's most-watched
  // owners and returns prayers ordered newest-first. Requires auth; treats
  // 401 (anonymous) or empty result as "no history yet" and returns [].
  try {
    const { data } = await api.get(`/prayers/for-you`, { params: { limit } });
    return data;
  } catch (err) {
    if (err?.response?.status === 401) return [];
    throw err;
  }
}
export async function fetchSharedPrayers({ limit = 60, sharer_scope = null } = {}) {
  // Prayers the current authenticated user has opened via a shared link,
  // sorted newest-first by open time. Anonymous callers get [].
  // `sharer_scope` narrows the ledger:
  //   • 'member'    — opens where the URL carried `?shared_by=<uid>`
  //   • 'anonymous' — opens where no sharer id was recorded
  //   • omit / null — the full history regardless of origin
  const params = { limit };
  if (sharer_scope) params.sharer_scope = sharer_scope;
  try {
    const { data } = await api.get(`/prayers/shared`, { params });
    return data;
  } catch (err) {
    if (err?.response?.status === 401) return [];
    throw err;
  }
}
export async function markSharedOpen(id, opts = {}) {
  // Record that the current user opened this prayer via a shared link.
  // `opts.shared_by` is the user id extracted from the incoming URL's
  // `?shared_by=…` query param — forwarded so the backend can credit
  // the sharer on the recipient's ledger row. Anonymous (401) is a
  // no-op so guests can still watch the prayer.
  const payload = {};
  if (opts.shared_by) payload.shared_by = opts.shared_by;
  try {
    const { data } = await api.post(`/prayers/${id}/shared-open`, payload);
    return data;
  } catch (err) {
    if (err?.response?.status === 401) return null;
    throw err;
  }
}
export async function removeSharedOpen(id) {
  // Remove the prayer from the current user's Shared tab ledger.
  // Idempotent — 404 or missing entry resolves as no-op.
  try {
    const { data } = await api.delete(`/prayers/${id}/shared-open`);
    return data;
  } catch (err) {
    if (err?.response?.status === 401) return null;
    throw err;
  }
}
export async function recordPrayerWatch(id) {
  // Notify the prayer's author that the current user has watched (prayed
  // with) their prayer. Backend is idempotent per (viewer, prayer) — the
  // FIRST watch inserts the "@viewer prayed for you" notification, all
  // subsequent watches by the same viewer are silent no-ops. Self-watches
  // (owner viewing their own prayer) are also filtered server-side.
  // Anonymous callers get a 401 (silent) — no notification is created
  // until the viewer has an identity to attribute the watch to.
  try {
    const { data } = await api.post(`/prayers/${id}/watch`);
    return data;
  } catch (err) {
    if (err?.response?.status === 401) return null;
    throw err;
  }
}
export async function hideFromRandom(id) {
  // Hide a prayer from the current user's Random tab. Persistent across
  // sessions via the backend `random_hidden` collection. Idempotent.
  try {
    const { data } = await api.post(`/prayers/${id}/hide-from-random`);
    return data;
  } catch (err) {
    if (err?.response?.status === 401) return null;
    throw err;
  }
}
export async function getMyPrayers() {
  const { data } = await api.get(`/prayers/mine`);
  return data;
}
export async function getMyLitCandles() {
  // Prayers the current user has lit a candle on, sorted by most-recent
  // candle-lighting time (descending). Backed by the prayer_candles ledger.
  const { data } = await api.get(`/prayers/my-lit-candles`);
  return data;
}
export async function getPrayer(id) {
  const { data } = await api.get(`/prayers/${id}`);
  return data;
}
export async function updatePrayer(id, updates) {
  const { data } = await api.patch(`/prayers/${id}`, updates);
  return data;
}
export async function replacePrayerImage(id, file) {
  const form = new FormData();
  form.append('image', file);
  const { data } = await api.post(`/prayers/${id}/image`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}
export async function deletePrayer(id) {
  const { data } = await api.delete(`/prayers/${id}`);
  return data;
}
export async function searchGallery({ page = 1, page_size = 24, q = '', category = '' } = {}) {
  const params = { page, page_size };
  if (q) params.q = q;
  if (category) params.category = category;
  const { data } = await api.get(`/prayers/gallery`, { params });
  return data;
}
export async function listGalleryCategories() {
  const { data } = await api.get(`/prayers/gallery/categories`);
  return Array.isArray(data?.items) ? data.items : [];
}
export async function fetchGalleryImageBlob(remoteUrl) {
  const { data } = await api.get(`/prayers/gallery/image`, {
    params: { url: remoteUrl },
    responseType: 'blob',
  });
  return data;
}
export async function fetchTtsBlob(prayerId) {
  const { data } = await api.get(`/prayers/${prayerId}/tts`, { responseType: 'blob' });
  return data;
}
export async function replacePrayerAudio(prayerId, audioBlob, durationSec = 0) {
  const form = new FormData();
  form.append('audio', audioBlob, `prayer-${Date.now()}.webm`);
  form.append('duration_sec', String(durationSec || 0));
  const { data } = await api.post(`/prayers/${prayerId}/audio`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}
export async function sendGuidedChat({ sessionId, message, history, signal, isSeed }) {
  const { data } = await api.post(`/prayers/guided-chat`, {
    session_id: sessionId,
    message,
    history: history || [],
    // Server ignores client history when this is true and calls the LLM with
    // ONLY [system, hidden_prompt] — guaranteeing no leakage from any prior
    // Speed Writer conversation.
    is_seed: !!isSeed,
  }, { signal });
  return data; // { reply, session_id, messages_sent }
}
export async function fetchGuidedChatSeed() {
  // Reads backend/config/initial_prayer_prompt.txt so operators can tweak the
  // hidden opener without a deploy. Returns { seed: string }.
  const { data } = await api.get(`/prayers/guided-chat/seed`);
  return data;
}
export async function resetGuidedChatSession(sessionId) {
  // Fire-and-forget ack that a fresh Speed Writer session is starting. The
  // backend chat handler is stateless, so this is purely an observability /
  // audit hook — it lets ops correlate a "new conversation" boundary in logs.
  const { data } = await api.post(`/prayers/guided-chat/reset`, { session_id: sessionId });
  return data;
}
export async function suggestPrayerTitle(text) {
  const { data } = await api.post(`/prayers/guided-title`, { text });
  return data; // { title }
}
export async function lightCandle(prayerId) {
  // Toggles the current user's candle on the prayer — at most 1 per user.
  // Returns `{candles_count, is_lit}` reflecting post-toggle state.
  const { data } = await api.post(`/prayers/${prayerId}/candle`);
  return data;
}
export async function getCandleStatus(prayerId) {
  // Fetch total count + whether the current viewer has already lit this
  // prayer's candle (anonymous → is_lit: false).
  const { data } = await api.get(`/prayers/${prayerId}/candle`);
  return data; // { candles_count, is_lit }
}
export async function recordPrayerView(prayerId) {
  // Called by WatchPrayer on mount. Server dedupes within a 30s window per
  // viewer so refreshing / re-mounting doesn't inflate the count.
  const { data } = await api.post(`/prayers/${prayerId}/view`);
  return data; // { plays_count, counted }
}

// ---------- Owner comments ----------
export async function listOwnerComments(prayerId) {
  const { data } = await api.get(`/prayers/${prayerId}/comments`);
  return data; // OwnerCommentOut[]
}
export async function createOwnerComment(prayerId, text) {
  const { data } = await api.post(`/prayers/${prayerId}/comments`, { text });
  return data;
}
export async function updateOwnerComment(commentId, text) {
  const { data } = await api.put(`/comments/${commentId}`, { text });
  return data;
}
export async function deleteOwnerComment(commentId) {
  const { data } = await api.delete(`/comments/${commentId}`);
  return data;
}
export async function participatePrayer(prayerId) {
  const { data } = await api.post(`/prayers/${prayerId}/participate`);
  return data;
}
export async function leavePrayer(prayerId) {
  const { data } = await api.delete(`/prayers/${prayerId}/participate`);
  return data;
}
export async function fetchLiveCounts(prayerIds = []) {
  // Batch fetch the live-praying counts for a set of prayer ids so
  // feed cards can poll the LIVE badge without one request per card.
  // Empty input short-circuits to avoid a pointless network call.
  if (!Array.isArray(prayerIds) || prayerIds.length === 0) return {};
  try {
    const { data } = await api.post(`/prayers/live-counts`, { ids: prayerIds });
    return data?.counts || {};
  } catch (err) {
    console.warn('fetchLiveCounts failed', err);
    return {};
  }
}

export async function getParticipants(prayerId) {
  const { data } = await api.get(`/prayers/${prayerId}/participants`);
  return data;
}

// ---------- Prayer Circles (follow authors) ----------
export async function getPublicUser(userId) {
  const { data } = await api.get(`/users/${userId}/public`);
  return data; // { user_id, handle, name, picture }
}

export async function getCircleStats(userId) {
  const { data } = await api.get(`/users/${userId}/circle-stats`);
  return data; // { user_id, total_prayers, total_watch_count, followers }
}

// ---------- Pinned Prayers (Circle-page curation) ----------
export async function getPinnedStatus(prayerId) {
  const { data } = await api.get(`/pinned-prayers/status`, { params: { prayer_id: prayerId } });
  return data; // { pinned, prayer_id }
}
export async function pinPrayer(prayerId) {
  const { data } = await api.post('/users/me/pinned-prayers', { prayer_id: prayerId });
  return data;
}
export async function unpinPrayer(prayerId) {
  const { data } = await api.delete(`/users/me/pinned-prayers/${prayerId}`);
  return data;
}
export async function listPinnedForUser(userId) {
  const { data } = await api.get(`/users/${userId}/pinned-prayers`);
  return Array.isArray(data) ? data : [];
}
export async function reorderPinnedPrayers(prayerIds) {
  const { data } = await api.post(`/users/me/pinned-prayers/reorder`, { prayer_ids: prayerIds });
  return data;
}
export async function reorderOwnCirclePrayers(prayerIds) {
  const { data } = await api.post(`/users/me/circle-prayer-order`, { prayer_ids: prayerIds });
  return data;
}
export async function getCircle(authorUserId) {
  const { data } = await api.get(`/circles/${authorUserId}`);
  return data; // { in_circle, member_count, is_self }
}
export async function toggleCircle(authorUserId) {
  const { data } = await api.post(`/circles/${authorUserId}/toggle`);
  return data;
}
export async function joinCircle(authorUserId) {
  const { data } = await api.post(`/circles/${authorUserId}/join`);
  return data; // { in_circle, already_member, member_count }
}
export async function leaveCircle(authorUserId) {
  const { data } = await api.delete(`/circles/${authorUserId}/leave`);
  return data; // { in_circle, member_count }
}

// ---------- Hashtag suggestions ----------
export async function fetchHashtagSuggestions({ q = '', limit = 10, exclude = [] } = {}) {
  const { data } = await api.get(`/hashtags/suggestions`, {
    params: { q, limit, exclude: (exclude || []).join(',') },
  });
  return Array.isArray(data) ? data : []; // [{tag, count}]
}
export async function fetchHashtagSuggestionsForContent({ content = '', limit = 6, exclude = [] } = {}) {
  const { data } = await api.get(`/hashtags/suggest-for-content`, {
    params: { content, limit, exclude: (exclude || []).join(',') },
  });
  return Array.isArray(data) ? data : []; // [{tag, count}]
}
export async function fetchHashtagSuggestionsAI({ content = '', limit = 6, exclude = [] } = {}) {
  // AI-generated hashtag suggestions based on the finalized prayer
  // text. Server routes through the Emergent LLM key and falls back
  // to keyword-matching on any AI hiccup so the response is never
  // empty when the text has recognisable themes.
  const { data } = await api.post(`/hashtags/suggest-ai`, {
    content,
    limit,
    exclude: exclude || [],
  });
  return Array.isArray(data) ? data : []; // [{tag, count}]
}
export async function fetchTrendingHashtags({ limit = 15, days = 7 } = {}) {
  const { data } = await api.get(`/hashtags/trending`, { params: { limit, days } });
  return Array.isArray(data) ? data : []; // [{tag, count}]
}
export async function getMyCircleAuthors() {
  const { data } = await api.get(`/circles/me/authors`);
  return data.authors || [];
}
export async function reportPrayer(prayerId, reason) {
  const { data } = await api.post(`/prayers/${prayerId}/report`, { reason: reason || '' });
  return data;
}
export async function adminListReports() {
  const { data } = await api.get(`/admin/reports`);
  return data;
}
export async function adminListUsers() {
  const { data } = await api.get(`/admin/users`);
  return data;
}
export async function adminDeletePrayer(prayerId, reason) {
  const body = reason ? { reason } : {};
  const { data } = await api.post(`/admin/prayers/${prayerId}/delete`, body);
  return data; // { ok, reason }
}
export async function adminBulkDeletePrayers(prayerIds, reason) {
  const { data } = await api.post(`/admin/prayers/bulk-delete`, {
    prayer_ids: prayerIds,
    ...(reason ? { reason } : {}),
  });
  return data; // { ok, deleted, missing, reason }
}
export async function adminDismissReports(prayerId) {
  const { data } = await api.delete(`/admin/reports/${prayerId}`);
  return data; // { ok, removed }
}
export async function adminBulkDismissReports(prayerIds) {
  const { data } = await api.post(`/admin/reports/bulk-dismiss`, { prayer_ids: prayerIds });
  return data; // { ok, removed }
}
export async function adminDeleteUser(userId) {
  const { data } = await api.delete(`/admin/users/${userId}`);
  return data; // { ok, user_id, prayers_deleted }
}
export async function adminListActivity(params = {}) {
  const { data } = await api.get(`/admin/activity`, { params });
  return data; // { items, count, event_types }
}
export async function adminActivityMetrics() {
  const { data } = await api.get(`/admin/activity/metrics`);
  return data; // { generated_at, event_types, last_24h, last_7d, last_30d }
}
export async function adminListAllPrayers() {
  const { data } = await api.get(`/admin/prayers`);
  return data; // { items, count }
}
export async function adminListCloudinaryCleanupFailures() {
  const { data } = await api.get(`/admin/cloudinary-cleanup-failures`);
  return data; // { items, count }
}
export async function adminRetryCloudinaryCleanup(prayerId) {
  const { data } = await api.post(`/admin/cloudinary-cleanup-failures/${prayerId}/retry`);
  return data; // { ok, resolved, error? }
}
export async function adminDismissCloudinaryCleanup(prayerId) {
  const { data } = await api.delete(`/admin/cloudinary-cleanup-failures/${prayerId}`);
  return data; // { ok }
}

// ---------- Feedback ----------
export async function submitFeedback({ message, email }) {
  const { data } = await api.post(`/feedback`, { message, email: email || null });
  return data;
}

// ---------- View history ----------
export async function fetchViewHistory() {
  const { data } = await api.get(`/prayers/history`);
  return data;
}
export async function clearViewHistory() {
  const { data } = await api.delete(`/prayers/history`);
  return data;
}
export async function deleteViewHistoryEntry(prayerId) {
  const { data } = await api.delete(`/prayers/history/${prayerId}`);
  return data;
}

// ---------- Dismissed prayers (X button on invite tabs) ----------
export async function randomFindPrayer() {
  const { data } = await api.get(`/prayers/random-find`);
  return data;
}
// ---------- Random-tab discovery ledger (per-user persistence) ----------
export async function getRandomDiscoveries() {
  const { data } = await api.get(`/random-discoveries`);
  return data; // { discoveries: [...], selected_id }
}
export async function addRandomDiscovery(prayerId) {
  const { data } = await api.post(`/random-discoveries/${prayerId}`);
  return data;
}
export async function removeRandomDiscovery(prayerId) {
  const { data } = await api.delete(`/random-discoveries/${prayerId}`);
  return data;
}
export async function selectRandomDiscovery(prayerId) {
  const { data } = await api.patch(`/random-discoveries/${prayerId}/select`);
  return data;
}
export async function dismissPrayer(prayerId) {
  const { data } = await api.post(`/prayers/${prayerId}/dismiss`);
  return data;
}
export async function undismissPrayer(prayerId) {
  const { data } = await api.delete(`/prayers/${prayerId}/dismiss`);
  return data; // { ok, removed }
}
export async function getDismissedPrayers() {
  const { data } = await api.get(`/prayers/dismissed`);
  return data;
}

// ---------- "To Random" tab — my outgoing random-share prayers ----------
export async function fetchSentRandom() {
  const { data } = await api.get(`/prayers/sent-random`);
  return data;
}
export async function adminListFeedback() {
  const { data } = await api.get(`/admin/feedback`);
  return data; // { items, count }
}
export async function adminDeleteFeedback(feedbackId) {
  const { data } = await api.delete(`/admin/feedback/${feedbackId}`);
  return data;
}
export async function adminListMisuseReports() {
  const { data } = await api.get(`/admin/misuse-reports`);
  return data; // { items, count }
}
export async function adminNotifyUser(userId, message, opts = {}) {
  const body = { message };
  if (opts.prayer_id) body.prayer_id = opts.prayer_id;
  const { data } = await api.post(`/admin/users/${userId}/notify`, body);
  return data;
}
// Admin — Resend email template preview / test-send.
export async function adminListEmailTemplates() {
  const { data } = await api.get(`/admin/email-templates`);
  return data;
}
export async function adminPreviewEmailTemplate(template_key, variables = {}) {
  const { data } = await api.post(`/admin/email-templates/preview`, {
    template_key, variables,
  });
  return data;
}
export async function adminSendEmailTemplateTest(template_key, to, variables = {}) {
  const { data } = await api.post(`/admin/email-templates/send-test`, {
    template_key, to, variables,
  });
  return data;
}

// Admin — observability rollups.
export async function adminObservabilitySummary(hours = 168) {
  const { data } = await api.get(`/admin/observability/summary`, { params: { hours } });
  return data;
}
export async function adminObservabilityRecent(kind, limit = 50) {
  const params = { limit };
  if (kind) params.kind = kind;
  const { data } = await api.get(`/admin/observability/recent`, { params });
  return data;
}

// Fire-and-forget telemetry — never awaited by callers, never throws.
// See `/api/telemetry/audio-failure` in backend for accepted fields.
// Silently no-ops if the browser blocks the request or the server 5xx's;
// telemetry must never punish the caller.
export function reportAudioFailure(payload = {}) {
  try {
    const body = {
      reason: payload.reason || 'unknown',
      label: payload.label || null,
      prayer_id: payload.prayer_id || null,
      phase: payload.phase || null,
      ua: (typeof navigator !== 'undefined' && navigator.userAgent) || null,
      error_message: payload.error_message || null,
      narration_pref: payload.narration_pref || null,
    };
    // Use `fetch` directly (not the shared `api` axios instance) so a
    // telemetry error can't trip the global 401 / retry interceptors.
    // `keepalive: true` lets the report land even if the page is
    // navigating away — Chrome / Safari both honour it for tiny POSTs.
    fetch(`${api.defaults.baseURL || ''}/telemetry/audio-failure`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).catch(() => { /* swallow */ });
  } catch { /* swallow */ }
}

/**
 * Generic business / UI event reporter — fire-and-forget, never throws.
 * Companion to `reportAudioFailure` but for anything else: prayer create
 * failures, share flow drops, moderation rejects, upload failures,
 * ErrorBoundary trips, etc.
 *
 * Server drops keys we don't understand and clamps every value, so
 * callers can toss anything short and useful in `meta`. The `kind`
 * string is the primary group-by dimension in the admin dashboard —
 * pick a dotted convention (e.g. `business.prayer.create_failed`,
 * `ui.error.render`, `ui.action.share_failed`) so patterns stay
 * greppable.
 *
 * Example:
 *   logEvent('business.prayer.create_failed', {
 *     level: 'error',
 *     error_message: err?.message,
 *     meta: { has_audio: !!audioBlob, char_count: text.length },
 *   });
 */
export function logEvent(kind, opts = {}) {
  if (!kind) return;
  try {
    const body = {
      kind: String(kind).slice(0, 64),
      level: opts.level || 'info',
      prayer_id: opts.prayer_id || null,
      route: opts.route || (typeof window !== 'undefined' ? window.location.pathname : null),
      ua: (typeof navigator !== 'undefined' && navigator.userAgent) || null,
      error_message: opts.error_message || null,
      meta: (opts.meta && typeof opts.meta === 'object') ? opts.meta : null,
    };
    fetch(`${api.defaults.baseURL || ''}/telemetry/event`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).catch(() => { /* swallow */ });
  } catch { /* swallow */ }
}
export async function sendThanks(notifId) {
  const { data } = await api.post(`/notifications/${notifId}/thanks`);
  return data;
}

/**
 * Post-moderation perspective analysis for the create-prayer flow.
 * Returns `{ is_first_person, third_person_text }`. If a moderation
 * failure or a rate-limit prevents analysis, the caller should treat
 * it as "no rewrite" and let the prayer save as-is.
 */
export async function checkPerspective({ title, description }) {
  const { data } = await api.post(`/prayers/perspective-check`, {
    title: title || '',
    description: description || '',
  });
  return data;
}
export async function getCircleFeed({ limit = 60, skip = 0 } = {}) {
  const { data } = await api.get(`/circles/me/feed`, { params: { limit, skip } });
  return data;
}
export async function markCircleSeen(authorUserId) {
  const { data } = await api.post(`/circles/${authorUserId}/seen`);
  return data;
}
export async function reorderCircles(authorIds) {
  const { data } = await api.post(`/circles/me/reorder`, { author_ids: authorIds });
  return data;
}

// ---------- Notifications ----------
export async function getNotifications({ limit = 40, skip = 0 } = {}) {
  const { data } = await api.get(`/notifications`, { params: { limit, skip } });
  return data; // { items, unread_count, last_read_at }
}
export async function markNotificationsRead() {
  const { data } = await api.post(`/notifications/mark-read`);
  return data; // { ok, last_read_at }
}
export async function deleteNotification(id) {
  // Hard-delete direct notifications; upsert a dismissal for synthetic
  // (circle-digest) items. Idempotent — safe to call twice on the same id.
  const { data } = await api.delete(`/notifications`, { params: { id } });
  return data; // { ok, removed, dismissed }
}

// ---------- Floating hearts (per-prayer live SSE) ----------
export async function sendPrayerHeart(prayerId) {
  const { data } = await api.post(`/prayers/${prayerId}/heart`);
  return data; // { ok, emitted, throttled? }
}
// Absolute SSE URL — EventSource can't share the axios instance's
// baseURL, so callers pass this directly to `new EventSource(...)`.
export function prayerStreamUrl(prayerId) {
  return `${API_BASE}/prayers/${prayerId}/stream`;
}


// ---------- Site-wide immersive effect settings ----------
export async function getImmersiveSettings() {
  const { data } = await api.get('/site-settings/immersive');
  return data; // { sparks_speed }
}
export async function updateImmersiveSettings(sparksSpeed) {
  const { data } = await api.put('/admin/site-settings/immersive', { sparks_speed: sparksSpeed });
  return data; // { sparks_speed }
}
